import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Stack } from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText";
import { DateTime } from "luxon";
import { useRecordContext, useRefresh } from "react-admin";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useMutation } from "react-query";
import { getAuthenticatedBackendClient } from "../../api/backend.api.js";
import { reportError } from "../../backoffice.utils";
import { DateInput } from "../../inputs/DateInput";
import { EurosInput } from "../../inputs/MoneyInput.js";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { DialogProps } from "../../misc/DialogProps";
import { formatMoney } from "../../misc/Money.js";
import { Row } from "../../misc/Row";
import { Student } from "../../providers/studentsProvider";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useDialog } from "../../hooks/useDialog";

type PrepaymentFormValues = {
  amount: number | null;
  manualPaymentDate: DateTime | null;
};

// Shows the PrepaidFormDialog and a ConfirmDialog afterwards
export const BankTransferPaymentDialogs = (
  props: {
    openDialog: () => void;
  } & DialogProps,
) => {
  const { open, onClose, openDialog } = props;
  const {
    isDialogOpen: isConfirmDialogOpen,
    openDialog: openConfirmDialog,
    closeDialog: closeConfirmDialog,
  } = useDialog();
  const form = useForm<PrepaymentFormValues>({
    defaultValues: {
      amount: 0,
      manualPaymentDate: null,
    },
  });

  return (
    <FormProvider {...form}>
      <PrepaidFormDialog open={open} onClose={onClose} onConfirm={openConfirmDialog} />
      <PrepaymentConfirmDialog
        open={isConfirmDialogOpen}
        onClose={() => {
          form.reset();
          closeConfirmDialog();
        }}
        onBack={() => {
          closeConfirmDialog();
          openDialog();
        }}
      />
    </FormProvider>
  );
};

type PrepaidFormDialogProps = {
  onConfirm: () => void;
} & DialogProps;

const PrepaidFormDialog = ({ open, onClose, onConfirm }: PrepaidFormDialogProps) => {
  const resetAndClose = () => {
    reset();
    onClose();
  };
  const { trigger, reset } = useFormContext<PrepaymentFormValues>();
  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth>
      <DialogTitle>Banküberweisung erfassen</DialogTitle>
      <DialogCloseButton onClick={resetAndClose} />
      <DialogContent>
        <PrepaymentForm />
      </DialogContent>
      <DialogActions>
        <Button
          endIcon={<NavigateNextIcon />}
          variant="outlined"
          onClick={async () => {
            const valid = await trigger();
            if (!valid) return;
            onClose();
            onConfirm();
          }}
        >
          Weiter
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function PrepaymentForm() {
  const { setValue } = useFormContext<PrepaymentFormValues>();
  return (
    <Stack gap="5px">
      <DialogContentText>
        <ol style={{ paddingLeft: "16px" }}>
          <li>Bitte Betrag eingeben, den der Kunde per Banküberweisung eingezahlt hat.</li>
          <li>Nach Bestätigung wird die Banküberweisung erfasst und dem Kundenkonto gutgeschrieben.</li>
        </ol>
      </DialogContentText>
      <FormControl>
        <Row sx={{ mt: 1.5, mb: 3 }} spacing={1}>
          <EurosInput
            label="Betrag"
            autoFocus
            source="amount"
            sx={{ "& .MuiInputBase-root": { minWidth: "150px", width: "150px" } }}
            validate={(value) => {
              if (value === null) {
                return "Betrag ist erforderlich";
              }
              if (value < 1) {
                return "Betrag muss mindestens 1 Euro sein";
              }
            }}
            onChange={(value) => setValue("amount", value)}
          />
          <DateInput<PrepaymentFormValues>
            source="manualPaymentDate"
            label="Zahlungsdatum (optional)"
            allowPastDates
            required={false}
            showClearButton
          />
        </Row>
      </FormControl>
    </Stack>
  );
}

type PrepaymentConfirmDialogProps = {
  onBack: () => void;
} & DialogProps;

const PrepaymentConfirmDialog = ({ open, onClose, onBack }: PrepaymentConfirmDialogProps) => {
  const student = useRecordContext<Student>();
  const form = useFormContext<PrepaymentFormValues>();
  const refresh = useRefresh();

  const total = form.watch("amount");

  async function createExternalBankTransferInvoice(values: PrepaymentFormValues): Promise<void> {
    if (values.amount === null || values.amount < 1) {
      throw new Error(`Amount must be at least 1. ${JSON.stringify(values)}`);
    }
    const backendClient = await getAuthenticatedBackendClient();
    await backendClient.invoice.invoiceCreateExternalBankTransferPaymentInvoiceCreate({
      requestBody: {
        user_id: student.autovioUserId,
        amount: { amount: values.amount.toString(), currency: "EUR" },
        driving_school_id: student.drivingSchoolId,
        ...(values.manualPaymentDate ? { manual_payment_date: values.manualPaymentDate.toISODate() ?? undefined } : {}),
      },
    });
  }

  const { mutate: executeMutation, isLoading } = useMutation(
    "externalBankTransferInvoice",
    createExternalBankTransferInvoice,
    {
      onSuccess: () => {
        onClose();
        refresh();
        showSuccessToast(`Banküberweisung erfolgreich erfasst.`);
      },
      onError: (error) => {
        reportError(
          `Failed to create external bank transfer invoice for student ${student.name} (${student.id})`,
          error,
        );
        showErrorToast(`Banküberweisung konnte nicht erfasst werden.`);
      },
    },
  );
  const onSubmit = (values: PrepaymentFormValues) => {
    executeMutation(values);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Banküberweisung erfassen</DialogTitle>
      <DialogCloseButton onClick={onClose} disabled={isLoading} />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing="1em">
            {total && (
              <DialogContentText>
                Banküberweisung von <b>{formatMoney(total)}</b> als Guthaben für <b>{student.name}</b> erfassen?
              </DialogContentText>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={onBack} disabled={isLoading}>
            Zurück
          </Button>
          <LoadingButton
            loading={isLoading}
            loadingPosition="start"
            variant="contained"
            onClick={form.handleSubmit(onSubmit)}
          >
            Banküberweisung erfassen
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
